import { ExternalLink, Tooltip, LegacyDataTableHeaderColumn, LegacyDataTableRow, LegacyDataTableRowColumn } from "@octopusdeploy/design-system-components";
import type { MeanTimeToRecoveryDeployment, MeanTimeToRecoveryTrends } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { formatDurationInWords } from "~/areas/insights/dataTransformation/stringHelpers";
import InternalLink from "~/components/Navigation/InternalLink";
import DateFormatter from "~/utils/DateFormatter";
import { TrendTable } from "./TrendTable/TrendTable";
import styles from "./TrendTables.module.less";
interface Props {
    spaceId: string;
    trends: MeanTimeToRecoveryTrends;
}
export function TimeToRecoveryTrendTables({ trends, spaceId }: Props) {
    return (<>
            <LongestTimeToRecovery data={trends.LongestTimeToRecovery} spaceId={spaceId}/>
            <LargestNumberOfDeploymentsUntilRecovery data={trends.LargestNumberOfDeploymentsUntilRecovery} spaceId={spaceId}/>
        </>);
}
interface DeploymentTableProps {
    spaceId: string;
    data: MeanTimeToRecoveryDeployment[];
}
function LongestTimeToRecovery({ data, spaceId }: DeploymentTableProps) {
    const hasTenants = data.some((d) => d.TenantName !== null);
    return (<TrendTable title="Longest time to recovery" isEmpty={data.length === 0} note={<span>
                    The successful deployment with the longest <ExternalLink href="InsightsMeanTimeToRecovery">mean time to recovery</ExternalLink> following a failure for each project, environment and tenant. The first 10 in descending order by mean
                    time to recovery are shown.
                </span>} headerContent={<LegacyDataTableRow>
                    <LegacyDataTableHeaderColumn>Project</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Environment</LegacyDataTableHeaderColumn>
                    {hasTenants && <LegacyDataTableHeaderColumn>Tenant</LegacyDataTableHeaderColumn>}
                    <LegacyDataTableHeaderColumn>Release</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Completed</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Time to Recovery</LegacyDataTableHeaderColumn>
                </LegacyDataTableRow>} bodyContent={<>
                    {data.map((r) => (<LegacyDataTableRow key={r.Id}>
                            <LegacyDataTableRowColumn>
                                <InternalLink to={links.projectRootRedirect.generateUrl({ spaceId, projectSlug: r.ProjectId })}>{r.ProjectName}</InternalLink>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn>{r.EnvironmentName}</LegacyDataTableRowColumn>
                            {hasTenants && <LegacyDataTableRowColumn>{r.TenantName ?? "Untenanted"}</LegacyDataTableRowColumn>}
                            <LegacyDataTableRowColumn>
                                <InternalLink to={links.releasePage.generateUrl({ spaceId, projectSlug: r.ProjectId, releaseVersion: r.ReleaseVersion })}>{r.ReleaseVersion}</InternalLink>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn>
                                <Tooltip content={DateFormatter.dateToShortFormat(r.CompletedTime) || undefined}>
                                    <InternalLink to={links.deploymentPage.generateUrl({ spaceId, projectSlug: r.ProjectId, releaseVersion: r.ReleaseVersion, deploymentId: r.Id })}>{DateFormatter.momentAgo(r.CompletedTime)}</InternalLink>
                                </Tooltip>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn>{formatDurationInWords(r.TimeToRecovery)}</LegacyDataTableRowColumn>
                        </LegacyDataTableRow>))}
                </>}/>);
}
function LargestNumberOfDeploymentsUntilRecovery({ data, spaceId }: DeploymentTableProps) {
    const hasTenants = data.some((d) => d.TenantName !== null);
    return (<TrendTable title="Largest number of deployments until recovery" isEmpty={data.length === 0} note={<span>
                    The successful deployment with the most <ExternalLink href="InsightsMeanTimeToRecovery">deployments until recovery</ExternalLink> following a failure for each project, environment and tenant. The first 10 in descending order by
                    the count are shown.
                </span>} headerContent={<LegacyDataTableRow>
                    <LegacyDataTableHeaderColumn>Project</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Environment</LegacyDataTableHeaderColumn>
                    {hasTenants && <LegacyDataTableHeaderColumn>Tenant</LegacyDataTableHeaderColumn>}
                    <LegacyDataTableHeaderColumn>Release</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Completed</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn className={styles.numberColumn}>Deployments until Recovery</LegacyDataTableHeaderColumn>
                </LegacyDataTableRow>} bodyContent={<>
                    {data.map((r) => (<LegacyDataTableRow key={r.Id}>
                            <LegacyDataTableRowColumn>
                                <InternalLink to={links.projectRootRedirect.generateUrl({ spaceId, projectSlug: r.ProjectId })}>{r.ProjectName}</InternalLink>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn>{r.EnvironmentName}</LegacyDataTableRowColumn>
                            {hasTenants && <LegacyDataTableRowColumn>{r.TenantName ?? "Untenanted"}</LegacyDataTableRowColumn>}
                            <LegacyDataTableRowColumn>
                                <InternalLink to={links.releasePage.generateUrl({ spaceId, projectSlug: r.ProjectId, releaseVersion: r.ReleaseVersion })}>{r.ReleaseVersion}</InternalLink>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn>
                                <Tooltip content={DateFormatter.dateToShortFormat(r.CompletedTime) || undefined}>
                                    <InternalLink to={links.deploymentPage.generateUrl({ spaceId, projectSlug: r.ProjectId, releaseVersion: r.ReleaseVersion, deploymentId: r.Id })}>{DateFormatter.momentAgo(r.CompletedTime)}</InternalLink>
                                </Tooltip>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn className={styles.numberColumn}>{r.DeploymentsUntilRecovery}</LegacyDataTableRowColumn>
                        </LegacyDataTableRow>))}
                </>}/>);
}
